"use client";

import { useState } from "react";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { startEmailChange, resendEmailChange, changePassword } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getApiErrorMessage, getApiFieldErrors } from "@/lib/api-error";

interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ProfileDialog({ open, onOpenChange }: ProfileDialogProps) {
  const { user } = useAuth();

  // email section state: "default" | "form" | "sent"
  const [emailView, setEmailView] = useState<"default" | "form" | "sent">("default");
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendFeedback, setResendFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  // password section state: "default" | "form" | "success"
  const [passwordView, setPasswordView] = useState<"default" | "form" | "success">("default");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  function handleOpenChange(val: boolean) {
    if (!val) {
      setEmailView("default");
      setNewEmail("");
      setEmailError("");
      setResendFeedback(null);
      setPasswordView("default");
      setCurrentPassword("");
      setNewPassword("");
      setCurrentPasswordError("");
      setNewPasswordError("");
    }
    onOpenChange(val);
  }

  async function handleResend() {
    setResendFeedback(null);
    setResendLoading(true);
    try {
      const res = await resendEmailChange({ new_email: newEmail });
      if (res.success) {
        setResendFeedback({ ok: true, msg: res.message ?? "Ponovno smo poslali link." });
      }
    } catch (err) {
      setResendFeedback({
        ok: false,
        msg: getApiErrorMessage(err, "Greška pri ponovnom slanju."),
      });
    } finally {
      setResendLoading(false);
    }
  }
  
  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setCurrentPasswordError("");
    setNewPasswordError("");
    setPasswordLoading(true);
    try {
      const res = await changePassword({ current_password: currentPassword, password: newPassword });
      if (res.success) {
        setPasswordView("success");
        setCurrentPassword("");
        setNewPassword("");
      } 
    } catch (err) {
      const fieldErrors = getApiFieldErrors(err);

      setCurrentPasswordError(fieldErrors.current_password?.[0] ?? "");
      setNewPasswordError(
        fieldErrors.password?.[0] ??
          getApiErrorMessage(err, "Došlo je do greške. Pokušajte ponovo."),
      );
    } finally {
      setPasswordLoading(false);
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailError("");
    setEmailLoading(true);
    try {
      const res = await startEmailChange({ new_email: newEmail });
      if (res.success) {
        setEmailView("sent");
      }
    } catch (err) {
      const fieldErrors = getApiFieldErrors(err);

      setEmailError(
        fieldErrors.new_email?.[0] ??
          getApiErrorMessage(err, "Došlo je do greške. Pokušajte ponovo."),
      );
    } finally {
      setEmailLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md rounded-2xl p-6">
        <DialogHeader className="mb-2">
          <DialogTitle className="text-xl font-bold text-[#18181b]">Moj profil</DialogTitle>
          <DialogDescription className="text-sm text-[#71717a]">
            Uredite svoje podatke.
          </DialogDescription>
        </DialogHeader>

        {/* Confirmation state replaces the entire body */}
        {emailView === "sent" ? (
          <div className="flex flex-col items-center gap-5 py-4 text-center">
            {/* Icon */}
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[#e4e4e7] bg-[#f4f4f5]">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#18181b]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <rect width="20" height="16" x="2" y="4" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                <polyline points="16 17 18 19 22 15" />
              </svg>
            </div>

            {/* Main message */}
            <p className="text-lg font-bold text-[#18181b]">
              Molimo provjerite Vašu e-poštu
            </p>

            {/* Supporting text */}
            <div className="flex flex-col gap-1">
              <p className="text-sm text-[#71717a]">
                Poslali smo link na{" "}
                <span className="font-medium text-[#18181b]">{newEmail}</span>.
              </p>
              <p className="text-sm text-[#71717a]">
                Ako ga ne možete pronaći, provjerite spam folder.
              </p>
            </div>

            {/* Resend */}
            {resendFeedback ? (
              <p className={`text-sm ${resendFeedback.ok ? "text-green-600" : "text-red-500"}`}>
                {resendFeedback.msg}
              </p>
            ) : (
              <p className="text-sm text-[#71717a]">
                Niste dobili link?{" "}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="font-medium text-[#18181b] underline underline-offset-2 hover:opacity-70 disabled:opacity-40 transition-opacity"
                >
                  {resendLoading ? "Slanje..." : "Pošalji ponovno"}
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">

            {/* Email section — default */}
            {emailView === "default" && (
              <div className="rounded-2xl bg-[#f4f4f5] p-4 flex flex-col gap-3">
                <span className="text-sm font-medium text-[#18181b]">Email adresa</span>
                <div className="rounded-full bg-white px-4 py-2.5 text-sm text-[#18181b] border border-[#e4e4e7] truncate">
                  {user?.email ?? ""}
                </div>
                <button
                  onClick={() => { setNewEmail(""); setEmailError(""); setEmailView("form"); }}
                  className="self-start rounded-full border border-[#e4e4e7] bg-white px-5 py-2 text-sm font-medium text-[#18181b] transition-colors hover:bg-[#fafafa]"
                >
                  Promijeni email adresu
                </button>
              </div>
            )}

            {/* Email section — form (expanded) */}
            {emailView === "form" && (
              <div className="rounded-2xl bg-[#f4f4f5] p-4 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => { setEmailView("default"); setEmailError(""); }}
                  className="self-start inline-flex items-center gap-2 rounded-full border border-[#e4e4e7] bg-white px-4 py-2 text-sm font-medium text-[#18181b] transition-colors hover:bg-[#fafafa]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Odustani
                </button>

                <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-[#18181b]">
                      Trenutna email adresa
                    </label>
                    <input
                      type="email"
                      value={user?.email ?? ""}
                      readOnly
                      className="rounded-full bg-white px-4 py-2.5 text-sm text-[#71717a] border border-[#e4e4e7] outline-none cursor-not-allowed"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-[#18181b]">
                      Nova email adresa
                    </label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => { setNewEmail(e.target.value); setEmailError(""); }}
                      required
                      placeholder="nova@email.com"
                      className="rounded-full bg-white px-4 py-2.5 text-sm text-[#18181b] border border-[#e4e4e7] outline-none focus:ring-2 focus:ring-[#18181b]/10"
                    />
                    {emailError && (
                      <p className="text-xs text-red-500 px-1">{emailError}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={emailLoading}
                    className="self-start rounded-full bg-[#18181b] px-5 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-80 disabled:opacity-50"
                  >
                    {emailLoading ? "Šalje se..." : "Pošalji verifikacijski kod"}
                  </button>
                </form>
              </div>
            )}

            {/* Password section — default */}
            {passwordView === "default" && (
              <div className="rounded-2xl bg-[#f4f4f5] p-4 flex flex-col gap-3">
                <span className="text-sm font-medium text-[#18181b]">Lozinka</span>
                <div className="rounded-full bg-white px-4 py-2.5 text-sm text-[#71717a] border border-[#e4e4e7] tracking-widest">
                  ••••••••••
                </div>
                <button
                  onClick={() => { setCurrentPassword(""); setNewPassword(""); setCurrentPasswordError(""); setNewPasswordError(""); setPasswordView("form"); }}
                  className="self-start rounded-full border border-[#e4e4e7] bg-white px-5 py-2 text-sm font-medium text-[#18181b] transition-colors hover:bg-[#fafafa]"
                >
                  Promijeni lozinku
                </button>
              </div>
            )}

            {/* Password section — form (expanded) */}
            {passwordView === "form" && (
              <div className="rounded-2xl bg-[#f4f4f5] p-4 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => { setPasswordView("default"); setCurrentPasswordError(""); setNewPasswordError(""); }}
                  className="self-start inline-flex items-center gap-2 rounded-full border border-[#e4e4e7] bg-white px-4 py-2 text-sm font-medium text-[#18181b] transition-colors hover:bg-[#fafafa]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Odustani
                </button>

                <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-3">
                  {/* Trenutna lozinka */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-[#18181b]">Trenutna lozinka</label>
                    <div className="relative">
                      <input
                        type={showCurrent ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => { setCurrentPassword(e.target.value); setCurrentPasswordError(""); }}
                        required
                        className="w-full rounded-full bg-white px-4 py-2.5 pr-11 text-sm text-[#18181b] border border-[#e4e4e7] outline-none focus:ring-2 focus:ring-[#18181b]/10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrent((v) => !v)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#71717a] hover:text-[#18181b] transition-colors"
                        aria-label={showCurrent ? "Sakrij lozinku" : "Prikaži lozinku"}
                      >
                        {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {currentPasswordError && (
                      <p className="text-xs text-red-500 px-1">{currentPasswordError}</p>
                    )}
                  </div>

                  {/* Nova lozinka */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-[#18181b]">Nova lozinka</label>
                    <div className="relative">
                      <input
                        type={showNew ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => { setNewPassword(e.target.value); setNewPasswordError(""); }}
                        required
                        className="w-full rounded-full bg-white px-4 py-2.5 pr-11 text-sm text-[#18181b] border border-[#e4e4e7] outline-none focus:ring-2 focus:ring-[#18181b]/10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNew((v) => !v)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#71717a] hover:text-[#18181b] transition-colors"
                        aria-label={showNew ? "Sakrij lozinku" : "Prikaži lozinku"}
                      >
                        {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {newPasswordError && (
                      <p className="text-xs text-red-500 px-1">{newPasswordError}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="self-start rounded-full bg-[#18181b] px-5 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-80 disabled:opacity-50"
                  >
                    {passwordLoading ? "Šalje se..." : "Promijeni lozinku"}
                  </button>
                </form>
              </div>
            )}

            {/* Password section — success */}
            {passwordView === "success" && (
              <div className="rounded-2xl bg-[#f4f4f5] p-4 flex flex-col gap-3">
                <span className="text-sm font-medium text-[#18181b]">Lozinka</span>
                <p className="text-sm text-green-600">Lozinka je uspješno promijenjena.</p>
                <button
                  onClick={() => setPasswordView("default")}
                  className="self-start rounded-full border border-[#e4e4e7] bg-white px-5 py-2 text-sm font-medium text-[#18181b] transition-colors hover:bg-[#fafafa]"
                >
                  Promijeni lozinku
                </button>
              </div>
            )}

          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
